<template>
    <div class="table-container">
        <b-table
            :items="goodsData"
            :fields="decodeFields"
            primary-key="id"
            bordered
            head-variant="light"
            no-border-collapse
        >
            <template #top-row="data">
                <td class="td-numbering"></td>
                <td class="td-numbering table-success">1</td>
                <td class="td-numbering table-danger">2</td>
                <td class="td-numbering table-info">3</td>
                <td class="td-numbering table-primary">4</td>
                <td class="td-numbering table-warning">5</td>
                <td class="td-numbering table-success">6</td>
                <td class="td-numbering table-danger">7</td>
                <td class="td-numbering table-info">8</td>
                <td class="td-numbering table-primary">9</td>
                <td class="td-numbering table-warning">10</td>
                <td class="td-numbering table-success">11</td>
                <td class="td-numbering table-danger">12</td>
                <td class="td-numbering table-info">13</td>
                <td class="td-numbering"></td>
            </template>

            <template #head(action)>
                <div class="text-center">
                    <b-form-checkbox
                        v-model="selectAllGoods"
                        @change="setAllItemsToDelete"
                        :unchecked-value="false"
                    />
                </div>
            </template>
            <template #cell(action)="data">
                <div class="text-center">
                    <b-form-checkbox
                        v-model="data.item.itemToDelete"
                        @input="(e) => { if (!e) selectAllGoods = false; }"
                        :value="true"
                        :unchecked-value="false"
                    />
                </div>
            </template>
            <template #cell(name_pers)="data">
                <b-form-input
                    v-if="variantAttribute"
                    :style="{ minWidth: '300px' }"
                    :value="data.item.name_pers"
                    @change="(v) => onDecodeFieldChange(data.item, 'name_pers', v)"
                ></b-form-input>
                <div v-else>{{ data.item.name_pers }}</div>
                <span v-if="data.item.isHasDouble || data.item.isHasOverlap" class="error dup-hint">
                    <i class="icon icon-danger decodes-error-icon"></i>
                    <div class="pop-up">
                        <p class="red-text">{{ getCommonText("not_valid") }}</p>
                        <template v-if="data.item.isHasDouble">
                            <p v-for="(conflict, idx) in data.item.dupConflicts" :key="'dup_' + idx">
                                {{ dupConflictText(conflict) }}
                            </p>
                        </template>
                        <p v-if="data.item.isHasOverlap" class="overlap-message">{{ overlapMessage(data.item) }}</p>
                    </div>
                </span>
                <p class="val-msg" v-if="!data.item.name_pers">
                    {{ getCommonText("must_be_filled") }}
                </p>
            </template>
            <template #cell(kato)="data">
                <div class="kato-cell">
                    <b-form-input
                        v-if="variantAttribute"
                        :value="data.item.katoSearch"
                        :placeholder="getFormText('search_kato_placeholder')"
                        @input="(v) => onKatoSearchInput(data.item, v)"
                    ></b-form-input>
                    <ul
                        class="kato-suggest-list"
                        v-if="data.item === katoSearchRow && (katoSuggestions.length || katoSearchDone)"
                    >
                        <li
                            v-for="opt in katoSuggestions"
                            :key="opt.id"
                            @click="onKatoSelected(data.item, opt)"
                        >
                            <span class="kato-suggest-code">{{ opt.code }}</span>
                            <span class="kato-suggest-info">
                                <span class="kato-suggest-name">{{ katoNameLabel(opt) }}</span>
                                <span class="kato-suggest-path">{{ katoPathLabel(opt) }}</span>
                            </span>
                        </li>
                        <li
                            v-if="katoSuggestionsTotal > katoSuggestions.length"
                            class="kato-suggest-footer"
                        >
                            {{ getFormText("kato_search_shown_of", { shown: katoSuggestions.length, total: katoSuggestionsTotal }) }}
                        </li>
                        <li
                            v-if="katoSearchDone && !katoSuggestions.length"
                            class="kato-suggest-footer"
                        >
                            {{ getFormText("kato_search_not_found") }}
                        </li>
                    </ul>
                    <div v-if="!variantAttribute">{{ data.item.katoLabel }}</div>
                    <i
                        v-if="data.item.kato && data.item.katoFullPath"
                        class="icon icon-info kato-hint"
                    >
                        <div class="pop-up">{{ data.item.katoFullPath }}</div>
                    </i>
                </div>
                <p class="val-msg" v-if="!data.item.kato">
                    {{ getCommonText("must_be_filled") }}
                </p>
            </template>
            <template #cell(trip_start_date)="data">
                <c-date-picker
                    v-if="variantAttribute"
                    :item="data.item"
                    :header="header"
                    startEnd="start"
                    dateField="trip_start_date"
                    counterpartField="trip_end_date"
                    :minYearFromHeader="true"
                    :allowEqualDates="true"
                    :highlightRange="true"
                    @input="onDecodeDateChanged(data.item)"
                ></c-date-picker>
                <div v-else>{{ data.item.trip_start_date }}</div>
                <p class="val-msg" v-if="!data.item.trip_start_date">
                    {{ getCommonText("must_be_filled") }}
                </p>
            </template>
            <template #cell(trip_end_date)="data">
                <div class="date-cell">
                    <c-date-picker
                        v-if="variantAttribute"
                        :item="data.item"
                        :header="header"
                        startEnd="end"
                        dateField="trip_end_date"
                        counterpartField="trip_start_date"
                        :minYearFromHeader="true"
                        :allowEqualDates="true"
                        :highlightRange="true"
                        @input="onDecodeDateChanged(data.item)"
                    ></c-date-picker>
                    <div v-else>{{ data.item.trip_end_date }}</div>
                    <i
                        v-if="isEndDateInvalid(data.item)"
                        class="icon icon-danger"
                    >
                        <div class="pop-up">{{ getFormText("end_before_start") }}</div>
                    </i>
                </div>
                <p class="val-msg" v-if="!data.item.trip_end_date">
                    {{ getCommonText("must_be_filled") }}
                </p>
            </template>
            <template #cell(goal)="data">
                <div class="bp-category-name">
                    <div>{{ data.item["goal_" + lng] }}</div>
                    <i
                        v-if="variantAttribute"
                        class="icon icon-pencil-edit thin-blue ml-3 pointer icon-18"
                        @click="showGoalModal(data.item)"
                    ></i>
                </div>
            </template>
            <template #cell(limit)="data">
                <div class="text-right">{{ $n(data.item.limit) }}</div>
                <div class="text-right money-hint" v-if="data.item.limit > 0">
                    ({{ $n(limitTengeAmount(data.item)) }} {{ getCommonText("tenge_short") }})
                </div>
            </template>
            <template #cell(daily_days)="data">
                <div class="text-right">{{ $n(data.item.daily_days) }}</div>
            </template>
            <template #cell(housing_days)="data">
                <div class="text-right">{{ $n(data.item.housing_days) }}</div>
            </template>
            <template #cell(daily_expense)="data">
                <div class="text-right">{{ $n(data.item.daily_expense) }}</div>
            </template>
            <template #cell(rent_expense)="data">
                <div class="rent-expense-cell">
                    <b-form-input
                        v-if="variantAttribute"
                        :disabled="data.item.limit <= 0 || data.item.housing_days <= 0"
                        type="number"
                        min="0"
                        step="0.01"
                        class="text-right numeric-input"
                        :value="data.item.rent_expense"
                        @change="(v) => onDecodeFieldChange(data.item, 'rent_expense', v)"
                        @blur="() => inputFixedVldtn(data.item, 'rent_expense', data.item.rent_expense, 2)"
                    ></b-form-input>
                    <div v-else>{{ $n(data.item.rent_expense) }}</div>
                    <span v-if="isRentOverLimit(data.item)" class="error rent-hint">
                        <i class="icon icon-danger"></i>
                        <div class="pop-up">
                            <p class="red-text">{{ getFormText("rent_over_limit_attention") }}</p>
                            <p>{{ getFormText("rent_over_limit", { limit: $n(rentLimitAmount(data.item)) }) }}</p>
                        </div>
                    </span>
                </div>
                <div class="text-right money-hint" v-if="data.item.limit > 0 && data.item.housing_days > 0">
                    ({{ getFormText("rent_limit_hint", { sum: $n(rentLimitAmount(data.item)) }) }})
                </div>
                <p
                    class="val-msg"
                    v-if="!(data.item.limit <= 0 || data.item.housing_days <= 0) && (!data.item.rent_expense || data.item.rent_expense <= 0)"
                >
                    {{ getCommonText("positive") }}
                </p>
            </template>
            <template #cell(transp_expense)="data">
                <b-form-input
                    v-if="variantAttribute"
                    type="number"
                    min="0"
                    step="0.01"
                    class="text-right numeric-input"
                    :value="data.item.transp_expense"
                    @change="(v) => onDecodeFieldChange(data.item, 'transp_expense', v)"
                    @blur="() => inputFixedVldtn(data.item, 'transp_expense', data.item.transp_expense, 2)"
                ></b-form-input>
                <div v-else>{{ $n(data.item.transp_expense) }}</div>
                <p class="val-msg" v-if="!data.item.transp_expense || data.item.transp_expense <= 0">
                    {{ getCommonText("positive") }}
                </p>
            </template>
            <template #cell(total_expense)="data">
                <div class="text-right">{{ $n(data.item.total_expense) }}</div>
            </template>
            <template #cell(files)="data">
                <span class="blue pointer underline" @click="onFilesClick(data.item)"
                    >({{ data.item.file_hash ? data.item.file_hash.split(";").length : 0 }})</span
                >
            </template>
            <template #head(more)>
                <div class="text-center">
                    <i
                        v-if="variantAttribute"
                        :title="getCommonText('del_selected')"
                        class="icon icon-close table-all-remove"
                        @click="deleteSelectedRows"
                    ></i>
                </div>
            </template>
            <template #cell(more)="data">
                <i
                    v-if="variantAttribute"
                    class="icon icon-clear table-remove"
                    @click="deleteDecodeRow(data.item)"
                ></i>
            </template>
            <template #bottom-row>
                <td colspan="7" class="text-right">{{ getCommonText("total") }}</td>
                <td class="text-right">{{ $n(totalDailyDays) }}</td>
                <td class="text-right">{{ $n(totalHousingDays) }}</td>
                <td class="text-right">{{ $n(totalDailyExpense) }}</td>
                <td class="text-right">{{ $n(totalRentExpense) }}</td>
                <td class="text-right">{{ $n(totalTranspExpense) }}</td>
                <td class="text-right">{{ $n(totalExpenseSum) }}</td>
                <td colspan="2"></td>
            </template>
        </b-table>
        <modal-files-management
            ref="modalFilesManagement"
            :allFiles="allFiles"
            :variantAttribute="variantAttribute"
            @showFileUploadModal="showFileUploadModal"
            @setNewFileHash="setNewFileHash"
            @triggDownloadFile="triggDownloadFile"
            @triggDownloadAllFiles="triggDownloadAllFiles"
        ></modal-files-management>
    </div>
</template>

<script>
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-vue/dist/bootstrap-vue.css";
import BpI18nHandlerMixin from "../mixins/bp-i18n-handler-mixin";
import CDatePicker from "./c-date-picker.vue";
import ModalFilesManagement from "./modal-files-management.vue";

export default {
    name: "FormTemplate13",
    components: { CDatePicker, ModalFilesManagement },
    mixins: [BpI18nHandlerMixin],
    props: {
        goodsData: { type: Array, required: true },
        variantAttribute: Boolean,
        header: Object,
        currCategory: Object,
        lng: String,
        isLoad: Boolean,
        isEndDateInvalid: Function,
        isRentOverLimit: Function,
        rentLimitAmount: Function,
        limitTengeAmount: Function,
        checkDecodeControls: Function,
        decodeLabel: Function,
        setDataChanged: Function,
        deleteDecodeRow: Function,
        deleteDecodeRows: Function,
        showGoalModal: Function,
        allFiles: Array,
        showFileUploadModal: Function,
        triggDownloadFile: Function,
        triggDownloadAllFiles: Function,
        safeDecimal: Function,
        makeToast: Function,
        formTextNamespace: { type: String, default: "form_01_161_v2." },
    },
    data() {
        return {
            katoSearchRow: null,
            katoSuggestions: [],
            katoSuggestionsTotal: 0,
            katoSearchDone: false,
            katoSearchTimeout: null,
            selectAllGoods: false,
        };
    },
    watch: {
        lng() {
            this.goodsData.forEach((row) => {
                if (row.kato) this.refreshKatoDisplay(row);
            });
        },
    },
    methods: {
        addItem() {
            if (!this.ifAllFieldsFilled() || this.goodsData.some((item) => item.isHasDouble || item.isHasOverlap)) return;
            const item = { id: new Date() * -1 };
            this.$set(item, "name_pers", "");
            this.$set(item, "kato", null);
            this.$set(item, "katoLabel", "");
            this.$set(item, "katoSearch", "");
            this.$set(item, "katoFullPath", "");
            this.$set(item, "trip_start_date", null);
            this.$set(item, "trip_end_date", null);
            this.$set(item, "goal_ru", "");
            this.$set(item, "goal_kk", "");
            this.$set(item, "limit", this.currCategory ? this.currCategory.limit : null);
            this.$set(item, "rent_expense", null);
            this.$set(item, "transp_expense", null);
            this.$set(item, "file_hash", null);
            this.$set(item, "isNew", true);
            this.$set(item, "rowToSave", true);
            this.$set(item, "isHasDouble", false);
            this.$set(item, "dupConflicts", []);
            this.$set(item, "isHasOverlap", false);
            this.$set(item, "overlapConflicts", []);
            this.$set(item, "itemToDelete", false);
            this.$emit("addGetParams", item);
            this.goodsData.push(item);
            this.setDataChanged(true);
        },

        ifAllFieldsFilled() {
            const emptyField = this.goodsData.some(
                (item) =>
                    !item.name_pers ||
                    !item.kato ||
                    !item.trip_start_date ||
                    !item.trip_end_date ||
                    (!item.rent_expense && !(item.limit <= 0 || item.housing_days <= 0)) ||
                    !item.transp_expense
            );
            return !emptyField;
        },

        onDecodeFieldChange(row, field, value) {
            this.$set(row, field, value);
            this.$set(row, "rowToSave", true);
            this.checkDecodeControls();
            this.setDataChanged(true);
        },

        onDecodeDateChanged(row) {
            if (row.housing_days <= 0 && row.rent_expense) {
                this.$set(row, "rent_expense", null);
            }
            this.$set(row, "rowToSave", true);
            this.checkDecodeControls();
            this.setDataChanged(true);
        },

        inputFixedVldtn(item, field, value, digit) {
            this.$nextTick(() => {
                const newVal = parseFloat(value) ? this.safeDecimal(value).toDecimalPlaces(digit).toNumber() : null;
                this.$set(item, field, newVal);
                this.$set(item, "rowToSave", true);
                this.setDataChanged(true);
            });
        },

        onKatoSearchInput(row, value) {
            this.$set(row, "katoSearch", value);
            this.$set(row, "katoFullPath", "");
            this.katoSearchRow = row;
            this.katoSearchDone = false;
            clearTimeout(this.katoSearchTimeout);
            if (!value) {
                this.katoSuggestions = [];
                this.katoSuggestionsTotal = 0;
                return;
            }
            this.katoSearchTimeout = setTimeout(() => this.searchKato(row, value), 600);
        },

        async searchKato(row, value) {
            try {
                const response = await fetch("/api-py/search-kato/", {
                    method: "POST",
                    headers: { "Content-Type": "application/json;charset=utf-8" },
                    body: JSON.stringify({
                        search: value,
                        local_category: this.currCategory ? this.currCategory.local_category : null,
                        variant_date_time: this.header.variant_date_time,
                        lng: this.lng,
                    }),
                });
                if (response.ok) {
                    const data = await response.json();
                    this.katoSuggestions = data.results;
                    this.katoSuggestionsTotal = data.total_count;
                    this.katoSearchDone = true;
                }
            } catch (error) {
                this.makeToast(
                    "danger",
                    `${this.getErrText("bad_request")} searchKato()`,
                    error.toString()
                );
            }
        },

        katoNameLabel(opt) {
            const nameField = this.lng === "kk" ? "name_kz" : "name_ru";
            return opt[nameField] || opt.name_ru;
        },

        katoPathLabel(opt) {
            const nameField = this.lng === "kk" ? "name_kz" : "name_ru";
            if (opt.path && opt.path.length) {
                return opt.path.map((p) => p[nameField] || p.name_ru).join(" → ");
            }
            return this.katoNameLabel(opt);
        },

        onKatoSelected(row, opt) {
            const label = this.katoNameLabel(opt);
            this.$set(row, "kato", opt.code);
            this.$set(row, "kato_name_ru", opt.name_ru);
            this.$set(row, "kato_name_kz", opt.name_kz);
            this.$set(row, "kato_path", opt.path);
            this.$set(row, "katoLabel", label);
            this.$set(row, "katoSearch", label);
            this.$set(row, "katoFullPath", this.katoPathLabel(opt));
            this.katoSuggestions = [];
            this.katoSuggestionsTotal = 0;
            this.katoSearchDone = false;
            this.katoSearchRow = null;
            this.$set(row, "rowToSave", true);
            this.checkDecodeControls();
            this.setDataChanged(true);
        },

        refreshKatoDisplay(row) {
            const opt = {
                name_ru: row.kato_name_ru,
                name_kz: row.kato_name_kz,
                path: row.kato_path,
            };
            this.$set(row, "katoLabel", this.katoNameLabel(opt));
            this.$set(row, "katoFullPath", this.katoPathLabel(opt));
            if (this.katoSearchRow !== row) {
                this.$set(row, "katoSearch", this.katoNameLabel(opt));
            }
        },

        onFilesClick(item) {
            this.allFiles.forEach((file) => {
                if (file.new_file) this.$set(file, "new_file", false);
            });
            this.$refs.modalFilesManagement.showModal(item);
        },

        setNewFileHash(id, codes) {
            const row = this.goodsData.find((item) => item.id === id);
            if (!row) return;
            this.$set(row, "file_hash", codes);
            this.$set(row, "rowToSave", true);
            this.setDataChanged(true);
        },

        sumMoneyField(field) {
            return this.goodsData
                .reduce((acc, row) => acc.plus(row[field] || 0), this.safeDecimal(0))
                .toDecimalPlaces(2)
                .toNumber();
        },

        setAllItemsToDelete(val) {
            this.goodsData.forEach((item) => this.$set(item, "itemToDelete", val));
        },

        formatDate(value) {
            if (!value) return "";
            const parts = value.toString().split("-");
            return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : value;
        },

        dupConflictText(conflict) {
            if (conflict.localCategory === null) return this.getFormText("duplicate_exists");
            return this.getFormText("duplicate_exists_in_decode", {
                decode: this.decodeLabel(this.currCategory.post, conflict.localCategory),
            });
        },

        katoNameForConflict(conflict) {
            return (this.lng === "kk" ? conflict.katoNameKz : conflict.katoNameRu) || conflict.katoNameRu || "";
        },

        overlapConflictLine(conflict) {
            const params = {
                start: this.formatDate(conflict.start),
                end: this.formatDate(conflict.end),
                kato: this.katoNameForConflict(conflict),
            };
            if (conflict.localCategory === null) {
                return this.getFormText("period_overlap_item", params);
            }
            return this.getFormText("period_overlap_item_in_decode", {
                ...params,
                decode: this.decodeLabel(this.currCategory.post, conflict.localCategory),
            });
        },

        overlapMessage(item) {
            const header = this.getFormText("period_overlap_header");
            const lines = item.overlapConflicts.map((c) => this.overlapConflictLine(c));
            return `${header}\n${lines.join(";\n")}.`;
        },

        deleteSelectedRows() {
            const selected = this.goodsData.filter((item) => item.itemToDelete);
            if (!selected.length) return;
            const msg = this.selectAllGoods ? this.getDecText("del_all_recs") : this.getDecText("del_selected_recs");
            this.deleteDecodeRows(selected, msg);
        },
    },
    computed: {
        getFormText() {
            return this.setFormText(this.formTextNamespace);
        },

        decodeFields() {
            return [
                { key: "action", label: " " },
                { key: "name_pers", label: this.getFormText("name_pers"), thStyle: "min-width: 180px" },
                { key: "kato", label: this.getFormText("direction"), thStyle: "min-width: 220px" },
                { key: "trip_start_date", label: this.getFormText("trip_start_date") },
                { key: "trip_end_date", label: this.getFormText("trip_end_date") },
                { key: "goal", label: this.getFormText("goal"), thStyle: "min-width: 160px" },
                { key: "limit", label: this.getFormText("limit_field") },
                { key: "daily_days", label: this.getFormText("daily_days") },
                { key: "housing_days", label: this.getFormText("housing_days") },
                { key: "daily_expense", label: this.getFormText("daily_expense") },
                { key: "rent_expense", label: this.getFormText("rent_expense") },
                { key: "transp_expense", label: this.getFormText("transp_expense") },
                { key: "total_expense", label: this.getFormText("total_expense") },
                { key: "files", label: this.getCommonText("files") },
                { key: "more", label: "" },
            ];
        },

        totalDailyDays() {
            return this.goodsData.reduce((acc, row) => acc + (row.daily_days || 0), 0);
        },

        totalHousingDays() {
            return this.goodsData.reduce((acc, row) => acc + (row.housing_days || 0), 0);
        },

        totalDailyExpense() {
            return this.sumMoneyField("daily_expense");
        },

        totalRentExpense() {
            return this.sumMoneyField("rent_expense");
        },

        totalTranspExpense() {
            return this.sumMoneyField("transp_expense");
        },

        totalExpenseSum() {
            return this.sumMoneyField("total_expense");
        },
    },
};
</script>

<style scoped>
.val-msg {
    font-size: 0.6rem;
    font-style: italic;
    color: red;
    margin: 2px 0 0 0;
}

.table-container ::v-deep td {
    vertical-align: top;
}

.numeric-input {
    min-width: 121px;
}

.numeric-input::-webkit-outer-spin-button,
.numeric-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.numeric-input[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
}

.money-hint {
    font-size: 0.7rem;
    opacity: 0.6;
}

.dup-hint {
    position: relative;
    display: inline-block;
    margin-left: 4px;
}

.dup-hint > i {
    position: static;
}

.dup-hint:hover .pop-up {
    opacity: 1;
    visibility: visible;
}

.overlap-message {
    white-space: pre-line;
}

.rent-hint {
    position: relative;
    display: inline-block;
    margin-left: 4px;
}

.rent-hint > i {
    position: static;
}

.rent-hint:hover .pop-up {
    opacity: 1;
    visibility: visible;
}

.bp-category-name {
    display: flex;
    justify-content: space-between;
}

.date-cell {
    display: flex;
    align-items: center;
    gap: 4px;
}

.kato-cell {
    position: relative;
    display: flex;
    align-items: center;
    gap: 4px;
}

.kato-hint {
    position: relative;
    color: #6087a0;
    cursor: pointer;
    flex-shrink: 0;
}

.kato-hint .pop-up {
    display: none;
    position: absolute;
    top: 20px;
    left: 0;
    z-index: 30;
    width: 240px;
    background: #fff;
    border: 1px solid #6087a0;
    border-radius: 4px;
    padding: 6px 8px;
    font-size: 0.75rem;
    color: #1c2a3e;
}

.kato-hint:hover .pop-up {
    display: block;
}

.rent-expense-cell {
    display: flex;
    align-items: center;
    gap: 4px;
}

.kato-suggest-list {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 20;
    background: #fff;
    border: 1px solid #dee2e6;
    max-height: 200px;
    overflow-y: auto;
    list-style: none;
    padding: 0;
    margin: 0;
    min-width: 320px;
}

.kato-suggest-list li {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    padding: 4px 8px;
    cursor: pointer;
}

.kato-suggest-list li:not(:last-child) {
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 10px;
}

.kato-suggest-list li:hover {
    background: #f1f6fb;
}

.kato-suggest-footer {
    cursor: default;
    color: #6c757d;
    font-size: 0.7rem;
    font-style: italic;
}

.kato-suggest-footer:hover {
    background: transparent;
}

.kato-suggest-code {
    flex: 0 0 auto;
    min-width: 64px;
    color: #6087a0;
    font-size: 0.8rem;
}

.kato-suggest-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.kato-suggest-name {
    font-weight: 600;
}

.kato-suggest-path {
    font-size: 0.7rem;
    color: #6c757d;
}

.icon-danger {
    position: relative;
    color: #d9534f;
    cursor: pointer;
}

.icon-danger .pop-up {
    display: none;
    position: absolute;
    top: 20px;
    left: 0;
    z-index: 30;
    width: 220px;
    background: #fff;
    border: 1px solid #d9534f;
    border-radius: 4px;
    padding: 6px 8px;
    font-size: 0.75rem;
    color: #1c2a3e;
}

.icon-danger:hover .pop-up {
    display: block;
}
</style>
